/**
 * buildProAdvisor
 * ----------------
 * LAP-767 — Adapter qui transforme les VRAIES données professionnelles de
 * l'utilisateur (page /professionnelle, endpoint getProfessionnelle) en un
 * objet exploitable par la page "Conseiller Pro / Formation"
 * (`ConseillerProPage`).
 *
 * Pattern strictement identique à `buildEcoAdvisor` (Conseiller Écologie) et
 * `buildTimeAdvisor` (Conseiller Temps) :
 *   - on agrège les cartes de /api/Professional/getProfessionnelle/
 *     (profil, compétences clés, soft skills, temps de formation/travail,
 *      opportunités) ;
 *   - on calcule un score global /100 + une décomposition par dimension ;
 *   - on produit un diagnostic (forces / faiblesses / risques / non évalué) ;
 *   - on liste des actions prioritaires (avec route Life) ;
 *   - on prépare des prompts pour le chat conseiller.
 *
 * Toutes les chaînes affichées passent par `t` (i18next) avec un
 * `defaultValue` inline pour rester compatibles avec les 8 langues Life — même
 * trame que conseillerTemps / conseillerEcologie (aucune clé JSON ajoutée).
 *
 * Contrairement à l'écologie, le « pro » marche « à l'endroit » : plus de
 * compétences, de soft skills et de formation continue → meilleur score.
 */

// LAP-767 — Identity fallback when no t function is provided.
const identityT = (key, opts = {}) => {
  if (opts && typeof opts.defaultValue === 'string') return opts.defaultValue
  return key
}

// LAP-767 — Pondération des dimensions (somme = 1). Les compétences (auto-éval
// métier) et la formation continue pèsent le plus : ce sont les leviers
// d'employabilité les plus directs. Le profil (cap professionnel) et les
// opportunités (dynamique de recherche) sont contextuels donc plus légers.
export const PRO_DIMENSION_WEIGHTS = {
  profil: 0.15,
  competences: 0.25,
  softskills: 0.2,
  formation: 0.25,
  opportunites: 0.15,
}

// LAP-767 — Cible de formation continue : ~5 % du temps travaillé consacré à se
// former (ordre de grandeur des obligations/recommandations de développement
// des compétences). Atteindre cette part → score 100.
export const PRO_FORMATION_TARGET_RATIO = 0.05
// Repli en heures absolues quand on ne peut pas calculer de ratio (aucune heure
// de travail loggée) : ~35 h/an = un parcours certifiant léger.
export const PRO_FORMATION_TARGET_HOURS = 35

// LAP-767 — Barème du questionnaire soft skills Life : 5 catégories × 4
// questions, réponses notées 1→5. Le score total renvoyé par le backend est
// donc déjà sur /100 (5 × 4 × 5), et chaque catégorie sur /20.
export const PRO_SOFTSKILL_TOTAL_MAX = 100
export const PRO_SOFTSKILL_CATEGORY_MAX = 20

// LAP-767 — Palette par dimension (cohérente avec la page bleue « pro »).
const CATEGORY_COLORS = {
  profil: '#7e9cdb',
  competences: '#5b9bff',
  softskills: '#9bb8f5',
  formation: '#5ad1b0',
  opportunites: '#f2b84b',
}

function num(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function safeArr(value) {
  return Array.isArray(value) ? value : []
}

function clamp(value, min = 0, max = 100) {
  return Math.max(min, Math.min(max, value))
}

function nonEmptyStr(value) {
  return typeof value === 'string' && value.trim().length > 0
}

// LAP-767 — Même barème de statut que les autres conseillers.
function statusFromScore(score) {
  if (score >= 70) return 'good'
  if (score >= 45) return 'warning'
  return 'critical'
}

/**
 * LAP-767 — Construit une map année → valeur à partir d'une série
 * `{ data: [], label: [] }` renvoyée par getProfessionnelle (by_year). Les
 * labels d'années peuvent être des nombres ou des chaînes.
 */
function yearSeriesToMap(series) {
  const data = safeArr(series?.data)
  const labels = safeArr(series?.label)
  const map = new Map()
  labels.forEach((lbl, i) => {
    const year = num(lbl)
    if (year > 0) map.set(year, num(data[i]))
  })
  return map
}

/**
 * LAP-767 — Score de formation continue.
 *   - Si on connaît les heures de travail ET de formation de l'année la plus
 *     récente : score = part de formation / cible (5 %), borné 0-100.
 *   - Sinon (formation loggée sans heures de travail) : repli sur les heures
 *     absolues vs cible (~35 h/an).
 * Renvoie { score, available, ratio, formationHours, workHours, year }.
 */
function computeFormation(tempsFormation, tempsTravail) {
  const formMap = yearSeriesToMap(tempsFormation?.by_year)
  const workMap = yearSeriesToMap(tempsTravail?.by_year)

  const allYears = new Set([...formMap.keys(), ...workMap.keys()])
  if (allYears.size === 0) {
    return { score: null, available: false, ratio: 0, formationHours: 0, workHours: 0, year: null }
  }
  const latestYear = Math.max(...allYears)
  const formationHours = num(formMap.get(latestYear))
  const workHours = num(workMap.get(latestYear))
  const denom = formationHours + workHours

  let ratio = 0
  let score
  if (denom > 0) {
    ratio = formationHours / denom
    score = clamp(Math.round((ratio / PRO_FORMATION_TARGET_RATIO) * 100))
  } else {
    // aucune heure ni travail ni formation cette année → repli absolu
    score = clamp(Math.round((formationHours / PRO_FORMATION_TARGET_HOURS) * 100))
  }

  return {
    score,
    available: true,
    ratio,
    formationHours: Math.round(formationHours * 10) / 10,
    workHours: Math.round(workHours * 10) / 10,
    year: latestYear,
  }
}

/**
 * LAP-767 — Score « dynamique d'opportunités » à partir du suivi des
 * candidatures (opportunite.data = [enregistré, envoyé, accepté, refusé]).
 *   - non évalué si aucune candidature suivie ;
 *   - si l'utilisateur n'a fait qu'enregistrer sans postuler → score bas (25) ;
 *   - sinon : 40 + 60 × taux de conversion (accepté / décidé). Les candidatures
 *     en attente comptent pour 0.5 (neutre).
 */
function computeOpportunites(opportunite) {
  const data = safeArr(opportunite?.data).map((v) => num(v))
  const saved = num(data[0])
  const sent = num(data[1])
  const accepted = num(data[2])
  const refused = num(data[3])
  const tracked = saved + sent + accepted + refused
  if (tracked <= 0) {
    return { score: null, available: false, sent, accepted, refused, saved, conversion: null }
  }
  const progressed = sent + accepted + refused
  if (progressed <= 0) {
    return { score: 25, available: true, sent, accepted, refused, saved, conversion: null }
  }
  const decided = accepted + refused
  const conversion = decided > 0 ? accepted / decided : 0.5
  const score = clamp(Math.round(40 + conversion * 60))
  return {
    score,
    available: true,
    sent,
    accepted,
    refused,
    saved,
    conversion: decided > 0 ? Math.round(conversion * 100) : null,
  }
}

/**
 * LAP-767 — Score de complétude du profil professionnel.
 * Disponible dès que le métier actuel est renseigné. Le score récompense la
 * clarté du cap : ancienneté + métiers cibles définis.
 */
function computeProfil(profil) {
  const hasJob = nonEmptyStr(profil?.User_Job)
  if (!hasJob) {
    return { score: null, available: false, components: { hasJob: false } }
  }
  // Composantes (somme = 100). User_Job est la condition d'évaluation et
  // apporte une base de 20.
  let score = 20
  if (nonEmptyStr(profil?.User_JobYear)) score += 25
  if (nonEmptyStr(profil?.future_job1)) score += 35
  if (nonEmptyStr(profil?.future_job2)) score += 10
  if (nonEmptyStr(profil?.future_job3)) score += 10
  return {
    score: clamp(score),
    available: true,
    components: {
      hasJob: true,
      hasJobYear: nonEmptyStr(profil?.User_JobYear),
      futureJobs: ['future_job1', 'future_job2', 'future_job3'].filter((k) =>
        nonEmptyStr(profil?.[k]),
      ).length,
    },
  }
}

/**
 * LAP-767 — Score « compétences clés » = moyenne de l'auto-évaluation (1→5,
 * exposée par le backend en data = note × 20, donc déjà sur /100).
 * Disponible uniquement quand les 5 compétences sont notées (is_complete).
 */
function computeCompetences(competences) {
  const data = safeArr(competences?.data).map((v) => num(v))
  const isComplete = Boolean(competences?.is_complete) && data.length > 0
  if (!isComplete) {
    return {
      score: null,
      available: false,
      hasJob: nonEmptyStr(competences?.current_job),
      missingProfile: Boolean(competences?.missing_profile),
    }
  }
  const avg = data.reduce((a, b) => a + b, 0) / data.length
  return {
    score: clamp(Math.round(avg)),
    available: true,
    hasJob: nonEmptyStr(competences?.current_job),
    missingProfile: false,
    skills: safeArr(competences?.skills),
  }
}

/**
 * LAP-767 — Score « soft skills » = score total du questionnaire (déjà /100).
 * Disponible uniquement quand le questionnaire est rempli (show).
 * Identifie aussi la catégorie la plus faible (pour le diagnostic).
 */
function computeSoftSkills(softskills) {
  const show = Boolean(softskills?.show)
  const data = safeArr(softskills?.data).map((v) => num(v))
  const labels = safeArr(softskills?.label)
  if (!show || data.length === 0) {
    return { score: null, available: false, weakest: null, total: 0 }
  }
  const total = num(softskills?.total_score)
  // catégorie la plus faible (sur /20) → cible de progression
  let weakest = null
  data.forEach((v, i) => {
    if (weakest == null || v < weakest.value) {
      weakest = { label: labels[i] || `#${i + 1}`, value: v }
    }
  })
  return {
    score: clamp(Math.round(total)),
    available: true,
    weakest,
    total,
  }
}

/**
 * buildProAdvisor — moteur de score professionnel / formation.
 *
 * @param {object} params
 * @param {Array}  params.cards - tableau renvoyé par
 *   /api/Professional/getProfessionnelle/ : [{ type, data }, ...].
 * @param {object} params.advice - dernière recommandation persistée (ou {}).
 * @param {object} params.user - profil utilisateur (pour le prénom).
 * @param {Function} params.t - fonction i18next.
 * @returns {object} agrégat consommé par ConseillerProPage.
 */
export function buildProAdvisor({ cards = [], advice = {}, user = {}, t = identityT } = {}) {
  const firstName = user?.first_name || user?.firstName || user?.prenom || ''

  const byType = (type) => {
    const card = safeArr(cards).find((c) => c?.type === type)
    return card?.data ?? {}
  }
  const profilData = byType('profilutilisateur')
  const competencesData = byType('competences')
  const softskillsData = byType('softskills')
  const tempsFormationData = byType('tempsformation')
  const tempsTravailData = byType('tempstravail')
  const opportuniteData = byType('opportunite')

  const profil = computeProfil(profilData)
  const competences = computeCompetences(competencesData)
  const softskills = computeSoftSkills(softskillsData)
  const formation = computeFormation(tempsFormationData, tempsTravailData)
  const opportunites = computeOpportunites(opportuniteData)

  // ── Dimensions ────────────────────────────────────────────────────────
  const dimensions = [
    {
      key: 'profil',
      label: t('conseillerPro.dimensions.profil', { defaultValue: 'Cap professionnel' }),
      score: profil.score,
      available: profil.available,
      weight: PRO_DIMENSION_WEIGHTS.profil,
      route: '/professionnelle?action=complete-profile',
      detail: t('conseillerPro.dimensions.profilDetail', {
        defaultValue:
          'Clarté de votre projet : métier actuel, ancienneté et métiers cibles définis.',
      }),
      dataSource: t('conseillerPro.dimensions.profilData', {
        defaultValue: 'Données : votre profil professionnel (métier actuel et cibles).',
      }),
      emptyHint: t('conseillerPro.dimensions.profilEmpty', {
        defaultValue:
          'Métier actuel non renseigné — complétez votre profil pour activer ce score.',
      }),
    },
    {
      key: 'competences',
      label: t('conseillerPro.dimensions.competences', { defaultValue: 'Compétences clés' }),
      score: competences.score,
      available: competences.available,
      weight: PRO_DIMENSION_WEIGHTS.competences,
      route: '/professionnelle',
      detail: t('conseillerPro.dimensions.competencesDetail', {
        defaultValue:
          'Auto-évaluation des 5 compétences clés de votre métier (note moyenne sur 100).',
      }),
      dataSource: t('conseillerPro.dimensions.competencesData', {
        defaultValue: 'Données : votre auto-évaluation des compétences clés.',
      }),
      emptyHint: competences.missingProfile
        ? t('conseillerPro.dimensions.competencesNoJob', {
            defaultValue:
              'Renseignez votre métier, puis générez et notez vos 5 compétences clés.',
          })
        : t('conseillerPro.dimensions.competencesEmpty', {
            defaultValue:
              'Compétences clés non évaluées — notez vos 5 compétences pour activer ce score.',
          }),
    },
    {
      key: 'softskills',
      label: t('conseillerPro.dimensions.softskills', { defaultValue: 'Soft skills' }),
      score: softskills.score,
      available: softskills.available,
      weight: PRO_DIMENSION_WEIGHTS.softskills,
      route: '/professionnelle/soft-skills',
      detail: t('conseillerPro.dimensions.softskillsDetail', {
        defaultValue:
          'Score du questionnaire soft skills (leadership, communication, organisation…).',
      }),
      dataSource: t('conseillerPro.dimensions.softskillsData', {
        defaultValue: 'Données : votre questionnaire soft skills.',
      }),
      emptyHint: t('conseillerPro.dimensions.softskillsEmpty', {
        defaultValue:
          'Questionnaire soft skills non rempli — complétez-le pour activer ce score.',
      }),
    },
    {
      key: 'formation',
      label: t('conseillerPro.dimensions.formation', { defaultValue: 'Formation continue' }),
      score: formation.score,
      available: formation.available,
      weight: PRO_DIMENSION_WEIGHTS.formation,
      route: '/professionnelle',
      detail: t('conseillerPro.dimensions.formationDetail', {
        defaultValue:
          'Part de votre temps consacrée à vous former (cible ~5 % du temps travaillé).',
      }),
      dataSource: t('conseillerPro.dimensions.formationData', {
        defaultValue: 'Données : vos heures de formation vs heures de travail.',
      }),
      emptyHint: t('conseillerPro.dimensions.formationEmpty', {
        defaultValue:
          'Aucune heure de travail ou de formation enregistrée — saisissez vos temps pour activer ce score.',
      }),
    },
    {
      key: 'opportunites',
      label: t('conseillerPro.dimensions.opportunites', { defaultValue: 'Dynamique d’opportunités' }),
      score: opportunites.score,
      available: opportunites.available,
      weight: PRO_DIMENSION_WEIGHTS.opportunites,
      route: '/parcourspro',
      detail: t('conseillerPro.dimensions.opportunitesDetail', {
        defaultValue:
          'Activité de recherche et taux de conversion de vos candidatures suivies.',
      }),
      dataSource: t('conseillerPro.dimensions.opportunitesData', {
        defaultValue: 'Données : votre suivi de candidatures.',
      }),
      emptyHint: t('conseillerPro.dimensions.opportunitesEmpty', {
        defaultValue:
          'Aucune candidature suivie — utile si vous êtes en recherche active.',
      }),
    },
  ].map((d) => ({
    ...d,
    status: d.available && d.score != null ? statusFromScore(d.score) : 'unknown',
  }))

  // ── Score global ──────────────────────────────────────────────────────
  // Moyenne pondérée sur les seules dimensions évaluées, poids re-normalisés.
  const evaluatedDims = dimensions.filter((d) => d.available && d.score != null)
  const evaluatedWeight = evaluatedDims.reduce((acc, d) => acc + d.weight, 0)
  const globalScore =
    evaluatedWeight > 0
      ? Math.round(
          evaluatedDims.reduce((acc, d) => acc + d.score * d.weight, 0) / evaluatedWeight,
        )
      : null

  // ── Diagnostic ────────────────────────────────────────────────────────
  const strengths = dimensions
    .filter((d) => d.available && d.status === 'good')
    .map((d) => ({ key: d.key, title: d.label, detail: d.detail }))
  const weaknesses = dimensions
    .filter((d) => d.available && d.status === 'warning')
    .map((d) => ({ key: d.key, title: d.label, detail: d.detail }))
  const risks = dimensions
    .filter((d) => d.available && d.status === 'critical')
    .map((d) => ({ key: d.key, title: d.label, detail: d.detail }))
  const notEvaluated = dimensions
    .filter((d) => !d.available)
    .map((d) => ({ key: d.key, title: d.label, detail: d.emptyHint }))

  // ── Décomposition : répartition du temps pro (travail vs formation) ────
  const breakdownRaw = [
    {
      key: 'travail',
      category: t('conseillerPro.breakdown.travail', { defaultValue: 'Travail' }),
      value: formation.workHours,
      color: '#5b9bff',
    },
    {
      key: 'formation',
      category: t('conseillerPro.breakdown.formation', { defaultValue: 'Formation' }),
      value: formation.formationHours,
      color: '#5ad1b0',
    },
  ]
  const breakdownTotal = breakdownRaw.reduce((acc, e) => acc + e.value, 0)
  const breakdownEntries = breakdownRaw
    .map((e) => ({
      ...e,
      value: Math.round(e.value * 10) / 10,
      percent: breakdownTotal > 0 ? Math.round((e.value / breakdownTotal) * 100) : 0,
    }))
    .sort((a, b) => b.value - a.value)

  // ── Actions prioritaires ──────────────────────────────────────────────
  const actionsPrioritaires = []
  const actionable = dimensions
    .filter((d) => d.available && d.score != null && d.status !== 'good')
    .sort((a, b) => a.score - b.score)
  actionable.forEach((d) => {
    actionsPrioritaires.push({
      id: `improve-${d.key}`,
      title: t('conseillerPro.actions.improveTitle', {
        defaultValue: `Renforcer : ${d.label.toLowerCase()}`,
        category: d.label,
      }),
      description: d.detail,
      impact: d.status === 'critical' ? 'high' : 'medium',
      route: d.route,
    })
  })
  notEvaluated.forEach((d) => {
    const dim = dimensions.find((x) => x.key === d.key)
    actionsPrioritaires.push({
      id: `complete-${d.key}`,
      title: t('conseillerPro.actions.completeTitle', {
        defaultValue: `Compléter : ${d.title.toLowerCase()}`,
        category: d.title,
      }),
      description: d.detail,
      impact: 'low',
      route: dim ? dim.route : '/professionnelle',
    })
  })

  // ── Recommandation persistée (texte) ──────────────────────────────────
  const recommendation = extractAdviceContent(advice)

  // ── Recommandations narratives (repli sans appel LLM) ─────────────────
  const recommendations = []
  if (formation.available) {
    const pct = Math.round(formation.ratio * 100)
    if (formation.ratio < PRO_FORMATION_TARGET_RATIO) {
      recommendations.push(
        t('conseillerPro.reco.formationLow', {
          defaultValue: `Vous consacrez ${pct}% de votre temps à la formation (cible ~5%). Planifier ne serait-ce qu'une formation courte cette année protège votre employabilité.`,
          pct,
        }),
      )
    } else {
      recommendations.push(
        t('conseillerPro.reco.formationGood', {
          defaultValue: `Bonne dynamique de formation (${pct}% de votre temps). Capitalisez en validant vos acquis (certification, badge) pour les rendre visibles.`,
          pct,
        }),
      )
    }
  }
  if (competences.available && competences.score != null && competences.score < 70) {
    recommendations.push(
      t('conseillerPro.reco.competences', {
        defaultValue: `Vos compétences clés sont auto-évaluées à ${competences.score}/100. Ciblez les 1 à 2 plus faibles : ce sont vos meilleurs leviers de progression.`,
        score: competences.score,
      }),
    )
  }
  if (softskills.available && softskills.weakest) {
    recommendations.push(
      t('conseillerPro.reco.softskills', {
        defaultValue: `Votre soft skill la plus perfectible est « ${softskills.weakest.label} » (${softskills.weakest.value}/20). Un petit effort ciblé y aura un impact rapide.`,
        category: softskills.weakest.label,
        value: softskills.weakest.value,
      }),
    )
  }
  if (!profil.available) {
    recommendations.push(
      t('conseillerPro.reco.profil', {
        defaultValue:
          'Renseignez votre métier actuel et vos métiers cibles : c’est la boussole qui oriente vos formations et vos candidatures.',
      }),
    )
  }
  if (opportunites.available && opportunites.conversion != null) {
    recommendations.push(
      t('conseillerPro.reco.opportunites', {
        defaultValue: `Taux de conversion de vos candidatures : ${opportunites.conversion}%. Soignez votre préparation aux entretiens pour l’améliorer.`,
        rate: opportunites.conversion,
      }),
    )
  }

  // ── Prompts pour le chat conseiller ────────────────────────────────────
  const suggestedPrompts = [
    t('conseillerPro.prompts.training', {
      defaultValue: 'Quelles formations pour progresser vers mon métier cible ?',
    }),
    t('conseillerPro.prompts.employability', {
      defaultValue: 'Comment améliorer mon employabilité cette année ?',
    }),
    t('conseillerPro.prompts.skills', {
      defaultValue: 'Sur quelles compétences dois-je me concentrer ?',
    }),
    t('conseillerPro.prompts.market', {
      defaultValue: 'Comment me situer face au marché de mon métier ?',
    }),
  ]

  return {
    userProfile: {
      firstName,
    },
    totals: {
      currentJob: nonEmptyStr(profilData?.User_Job) ? profilData.User_Job : null,
      jobYear: nonEmptyStr(profilData?.User_JobYear) ? profilData.User_JobYear : null,
      futureJobsCount: profil.components?.futureJobs || 0,
      formationRatioPct: Math.round(formation.ratio * 100),
      formationHours: formation.formationHours,
      workHours: formation.workHours,
      softSkillsTotal: softskills.total,
      competenceAvg: competences.score,
      applicationsSent: opportunites.sent,
      conversionRate: opportunites.conversion,
      year: formation.year,
    },
    metrics: {
      formationHours: formation.formationHours,
      workHours: formation.workHours,
      softSkillsTotal: softskills.total,
      competenceAvg: competences.score,
    },
    proScore: {
      global: globalScore,
      dimensions,
      evaluatedCount: evaluatedDims.length,
      totalCount: dimensions.length,
    },
    diagnostic: {
      strengths,
      weaknesses,
      risks,
      notEvaluated,
    },
    breakdown: {
      entries: breakdownEntries,
      total: Math.round(breakdownTotal * 10) / 10,
    },
    actionsPrioritaires,
    recommendation,
    recommendations,
    suggestedPrompts,
  }
}

/**
 * LAP-767 — Extrait le texte de recommandation d'une ligne persistée.
 * Tolérant : `content` brut, ou `data` JSON sérialisé.
 */
export function extractAdviceContent(advice) {
  if (!advice || typeof advice !== 'object') return ''
  if (typeof advice.content === 'string' && advice.content.trim()) {
    return advice.content.trim()
  }
  if (typeof advice.data === 'string' && advice.data.trim()) {
    try {
      const parsed = JSON.parse(advice.data)
      if (typeof parsed === 'string') return parsed
      if (parsed && typeof parsed.summary === 'string') return parsed.summary
      if (parsed && typeof parsed.content === 'string') return parsed.content
    } catch (_) {
      return advice.data.trim()
    }
  }
  return ''
}

export { CATEGORY_COLORS }
